/**
 * Data lineage tracking types.
 * Logged by the pipeline executor, queried for audit/debug.
 */

import { Schema } from 'effect';

import {
  InputPortSchema,
  OutputPortSchema,
  ParserCategorySchema,
  ParserOutputSchema,
  ParserUsageSchema,
  type ParserCategory,
  type InputPort,
  type OutputPort,
  type ParserOutput,
} from './parser';

const optionalMutable = <S extends Schema.Constraint>(schema: S) =>
  Schema.mutableKey(Schema.optionalKey(schema));
const mutableArray = <S extends Schema.Constraint>(schema: S) =>
  Schema.mutable(Schema.Array(schema));
const openRecord = Schema.Record(Schema.String, Schema.Unknown);

export interface LineageRecord {
  id: string;
  stepIndex: number;
  vendorId: string;
  category: ParserCategory;
  input: {
    ports: InputPort[];
    pdfSizeBytes?: number;
    pageImageCount?: number;
    parameters?: Record<string, unknown>;
  };
  output: {
    ports: OutputPort[];
    success: boolean;
    pageCount?: number;
    blockCount?: number;
    entityCount?: number;
    costUsd?: number;
    durationMs: number;
    error?: string;
    /** Keys present in ParserOutput.debug — index for querying without reading full blob */
    debugKeys?: string[];
    /** Model used (from parsedResult.metadata.model) */
    model?: string;
    usage?: ParserOutput['usage'];
    /** Whether pre-normalization rawPages were stored */
    hasRawPages?: boolean;
  };
  startedAt: number;
  completedAt: number;
  /** Unmodified vendor output — stored in vendor_raw, stripped before hash chain. */
  rawOutput?: ParserOutput;
}

export const LineageRecordSchema: Schema.Codec<LineageRecord, unknown> = Schema.Struct({
  id: Schema.String.pipe(Schema.mutableKey),
  stepIndex: Schema.Natural.pipe(Schema.mutableKey),
  vendorId: Schema.String.pipe(Schema.mutableKey),
  category: ParserCategorySchema.pipe(Schema.mutableKey),
  input: Schema.Struct({
    ports: mutableArray(InputPortSchema).pipe(Schema.mutableKey),
    pdfSizeBytes: optionalMutable(Schema.Natural),
    pageImageCount: optionalMutable(Schema.Natural),
    parameters: optionalMutable(openRecord),
  }).pipe(Schema.mutableKey),
  output: Schema.Struct({
    ports: mutableArray(OutputPortSchema).pipe(Schema.mutableKey),
    success: Schema.Boolean.pipe(Schema.mutableKey),
    pageCount: optionalMutable(Schema.Natural),
    blockCount: optionalMutable(Schema.Natural),
    entityCount: optionalMutable(Schema.Natural),
    costUsd: optionalMutable(Schema.Finite),
    durationMs: Schema.Finite.pipe(Schema.mutableKey),
    error: optionalMutable(Schema.String),
    debugKeys: optionalMutable(mutableArray(Schema.String)),
    model: optionalMutable(Schema.String),
    usage: optionalMutable(ParserUsageSchema),
    hasRawPages: optionalMutable(Schema.Boolean),
  }).pipe(Schema.mutableKey),
  startedAt: Schema.Finite.pipe(Schema.mutableKey),
  completedAt: Schema.Finite.pipe(Schema.mutableKey),
  rawOutput: optionalMutable(ParserOutputSchema),
});

export interface PipelineStep {
  category: ParserCategory;
  vendorId?: string;
  requiresCapability?: string;
  optional?: boolean;
  parameters?: Record<string, unknown>;
}

export const PipelineStepSchema: Schema.Codec<PipelineStep> = Schema.Struct({
  category: ParserCategorySchema.pipe(Schema.mutableKey),
  vendorId: optionalMutable(Schema.String),
  requiresCapability: optionalMutable(Schema.String),
  optional: optionalMutable(Schema.Boolean),
  parameters: optionalMutable(openRecord),
});

export interface PipelineConfig {
  name: string;
  steps: PipelineStep[];
}

export const PipelineConfigSchema: Schema.Codec<PipelineConfig> = Schema.Struct({
  name: Schema.String.pipe(Schema.mutableKey),
  steps: mutableArray(PipelineStepSchema).pipe(Schema.mutableKey),
});

export const DEFAULT_PIPELINE: PipelineConfig = Schema.decodeUnknownSync(PipelineConfigSchema)({
  name: 'default',
  steps: [
    { category: 'ocr' },
    { category: 'entity', optional: true },
    { category: 'projection', optional: true },
  ],
});
